// ErrorBoundary — per W2 §3.3.2 #9.
// Class component (React 18 class-component contract); <PageErrorBoundary> wrapper
// applied at route-definition level in App.tsx.

import { Component, type ErrorInfo, type ReactNode } from "react";
import { AlertTriangle } from "lucide-react";
import { ErrorState } from "@/components/primitives/ErrorState";
import { PageHero } from "@/components/primitives/PageHero";
import {
  ERROR_BOUNDARY_GENERIC_TITLE,
  ERROR_BOUNDARY_RETRY_LABEL,
  ERROR_BOUNDARY_SUBTITLE,
  ERROR_BOUNDARY_TITLE_TEMPLATE,
} from "@/lib/i18n/errors";

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Optional label rendered into the error header (route name). */
  label?: string;
  /** Custom fallback. If omitted, a default ErrorState page renders. */
  fallback?: (error: Error, reset: () => void) => ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Per v2 §3.3.2 #9: console.error seam — replace with telemetry sink later.
    console.error("[ErrorBoundary]", { error, componentStack: info.componentStack });
  }

  reset = (): void => {
    this.setState({ error: null });
  };

  render(): ReactNode {
    const { error } = this.state;
    if (error === null) {
      return this.props.children;
    }
    if (this.props.fallback) {
      return this.props.fallback(error, this.reset);
    }
    return (
      <div data-component="ErrorBoundary" data-default="true">
        <PageHero
          title={ERROR_BOUNDARY_TITLE_TEMPLATE(this.props.label ?? null)}
          subtitle={ERROR_BOUNDARY_SUBTITLE}
        />
        <div className="px-4 pt-6">
          <ErrorState
            icon={<AlertTriangle className="w-6 h-6" aria-hidden />}
            title={ERROR_BOUNDARY_GENERIC_TITLE}
            description={error.message}
            onRetry={this.reset}
            retryLabel={ERROR_BOUNDARY_RETRY_LABEL}
          />
        </div>
      </div>
    );
  }
}

/** Convenience wrapper for `<App><Route><PageErrorBoundary label="登入">{...}</PageErrorBoundary></Route>...` */
export function PageErrorBoundary({ children, label }: { children: ReactNode; label?: string }) {
  return (
    <ErrorBoundary
      label={label}
      fallback={(error, reset) => (
        <div data-component="PageErrorBoundary" data-label={label ?? ""}>
          <PageHero
            title={ERROR_BOUNDARY_TITLE_TEMPLATE(null)}
            subtitle={label ? `(${label})` : undefined}
          />
          <div className="px-4 pt-6">
            <ErrorState
              icon={<AlertTriangle className="w-6 h-6" aria-hidden />}
              title={ERROR_BOUNDARY_GENERIC_TITLE}
              description={error.message}
              onRetry={reset}
              retryLabel={ERROR_BOUNDARY_RETRY_LABEL}
            />
          </div>
        </div>
      )}
    >
      {children}
    </ErrorBoundary>
  );
}
